/** The assurance queue — **gate 27 / ASR-02**: what a provider asked, what they were told, and who
 * closed it.
 *
 * Five routes existed and no screen called them, so an Auditor could be given a role and had nowhere
 * to use it. This is the screen; it adds no rule the server does not already hold.
 *
 * **A table page (ADR-0212, P10).** The queue is one grid of requests with a filter line and three
 * tiles; opening a row shows the request as it always was (its question, the whole thread, the forms)
 * in a drawer, and raising a request is a drawer too. The explanations moved behind info controls.
 *
 * ## What it is careful about
 *
 * **The thread is the deliverable.** Every response is shown, oldest first, including answers since
 * amended. The acceptance clause is that *"they said X and then said Y"* stays visible, so a card
 * that showed the latest answer would satisfy the clause on paper and not in the file a provider
 * reads. Nothing here collapses a thread.
 *
 * **Answered is not resolved, and the screen says so in words.** `answered` is the organisation's
 * claim that it has replied; `resolved` is the **requester's** judgement that the reply was enough;
 * `withdrawn` is a third fact and is not evidence that anything was tested. Colour only tints an
 * edge (design rule 8).
 *
 * **A role is told who can, never shown a control that predictably refuses** (design rule 6,
 * `KEEP.md` 17). `standing()` returns the sentence for each thing a person may not do, and the
 * sentence replaces the control. The server stays the authority; this only stops a Viewer being
 * offered a button that 403s.
 *
 * **A failed read is a failure.** The list is read by `useAssuranceQueue`, which throws, and
 * `Loaded` renders loading, failed and empty as three different things. An empty queue and an
 * unreadable one are opposite claims about an engagement.
 *
 * **An engaged Auditor's list is shorter than the queue, and the screen says so.** The server
 * filters rather than refuses (ADR-0029): a list is the one shape where an engagement narrows. So
 * the sentence beside it names what is not shown, because a short list reads as a short queue.
 * The same applies to a citation: the company answers under its own scope, so an answer can name a
 * record from a period the auditor was not engaged for, and the server withholds that id and says
 * it did. This screen prints the withholding rather than a blank.
 *
 * **Rule 4's habit, and why here.** `raised_by`, `responded_by` and `closed_by` are identifiers and
 * stay identifiers. The assurance tables are not the ledger and could carry names; they do not
 * because a name beside a statement in an assurance file is the one thing in it that an erasure
 * request would later change. Whether an auditor should be shown names is a product and legal
 * question (counsel, B-02) and is **not** decided by making this screen more useful.
 *
 * ## Not here
 *
 * **The ledger.** A scoped auditor reading the chain, clamped to their engagement, is not built;
 * see `BUILD/blocked/S13-auditor.md`. The Audit ledger screen still refuses an engaged Auditor and
 * says why, and this screen links to nothing that would imply otherwise.
 */

import { ClipboardText, DownloadSimple } from "@phosphor-icons/react";
import { Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import { apiUrl } from "../api/client";
import { useReader } from "../api/profile";
import type { AssuranceRequest, AssuranceResponse, RecordOption, RequestKindName } from "../api/assurance";
import {
  citationWithheld,
  isLive,
  KINDS,
  MAY_RAISE,
  MAY_READ,
  standing,
  UUID_PATTERN,
  useAssuranceQueue,
  useRaiseRequest,
  useRecordOption,
  useRespond,
  useResolve,
  useWithdraw,
} from "../api/assurance";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import {
  ActionButton,
  Drawer,
  EMPTY_FILTERS,
  EmptyAction,
  EmptyState,
  FilterLine,
  GridTable,
  InfoPopover,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { instantDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { RecordPicker } from "./AssuranceRecordPicker";
import { useGridSort } from "./gridSort";
import { ScreenFrame } from "./ScreenFrame";
import shared from "./Screen.module.css";
import own from "./Assurance.module.css";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type Filter = "all" | "open" | "answered" | "resolved" | "withdrawn";

const STATES: ReadonlyArray<Exclude<Filter, "all">> = ["open", "answered", "resolved", "withdrawn"];

/** The four states in a reader's words. **Each says whose move it is**, because the difference
 * between the middle two is the one the module turns on. */
const STATE: Record<string, { word: string; whose: string; tone: StatusTone }> = {
  open: { word: "Open", whose: "waiting for the organisation to answer", tone: "warn" },
  answered: {
    word: "Answered",
    whose: "waiting for whoever asked to say whether that was enough",
    tone: "info",
  },
  resolved: { word: "Resolved", whose: "whoever asked accepted the answer", tone: "ok" },
  withdrawn: {
    word: "Withdrawn",
    whose: "whoever asked no longer needs it, which is not evidence that anything was tested",
    tone: "neutral",
  },
};

const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS.map((k) => [k.kind, k.label]));

export function AssuranceScreen() {
  return (
    <RequireSession>
      <AssuranceBody />
    </RequireSession>
  );
}

function AssuranceBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const [line, setLine] = useState<FilterState>(EMPTY_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [raiseDismissed, setRaiseDismissed] = useState(false);
  const arrived: unknown = useSearch({ strict: false }).record;

  //: `undefined` while `/auth/me` is in flight, which is **not** "may not read".
  const mayRead = me ? MAY_READ.includes(me.role) : undefined;
  const queue = useAssuranceQueue(mayRead === true);
  const role = me?.role ?? "";
  const engaged = role === "auditor";
  const mayRaise = me ? MAY_RAISE.includes(role) : false;
  //: **Arrived at from a record** (gate 27): "Raise a question about this record" carries the record's
  //: id here, so the form opens with it already in; a hand-edited address that is not an id opens
  //: nothing by itself.
  const arrivedFromRecord = typeof arrived === "string" && UUID_PATTERN.test(arrived.trim());
  const raiseShown = mayRaise && (raiseOpen || (arrivedFromRecord && !raiseDismissed));

  // A failed refresh keeps the last queue in the cache; it is not counted as the queue.
  const known = queue.error ? undefined : queue.data;
  const waitingOnCompany = known?.filter((r) => r.state === "open").length ?? 0;
  const waitingOnReviewer = known?.filter((r) => r.state === "answered").length ?? 0;
  const resolved = known?.filter((r) => r.state === "resolved").length ?? 0;
  const withdrawn = known?.filter((r) => r.state === "withdrawn").length ?? 0;

  const requests = queue.data ?? [];
  const stateFilter = (line.values.state ?? [])[0];
  const needle = line.search.trim().toLowerCase();
  const rows = requests.filter(
    (r) =>
      (stateFilter === undefined || r.state === stateFilter) &&
      (!needle ||
        `${KIND_LABEL[r.kind] ?? r.kind} ${r.reference ?? ""} ${r.question}`.toLowerCase().includes(needle)),
  );
  const { sorted, sort, setSort } = useGridSort(rows, {
    request: (r) => KIND_LABEL[r.kind] ?? r.kind,
    state: (r) => STATE[r.state]?.word ?? r.state,
    raised: (r) => r.raised_at,
    responses: (r) => r.responses.length,
  });
  //: Looked up in the current queue on every render, so a write that re-reads it shows the fresh
  //: response beside the fresh state and never a stale card (the drawer holds an id, not a copy).
  const opened = openId === null ? undefined : requests.find((r) => r.id === openId);

  const columns: GridColumn<AssuranceRequest>[] = [
    {
      id: "request",
      header: "Request",
      sortable: true,
      rowHeader: true,
      cell: (r) => (
        <button
          type="button"
          className={shared.fixLink}
          data-testid="assurance-open"
          aria-label={`Open the request: ${r.question}`}
          onClick={() => setOpenId(r.id)}
        >
          {KIND_LABEL[r.kind] ?? r.kind}
          {r.reference ? ` ${r.reference}` : ""}
        </button>
      ),
    },
    { id: "question", header: "Question", cell: (r) => r.question },
    {
      id: "state",
      header: "State",
      sortable: true,
      cell: (r) => {
        const state = STATE[r.state];
        return (
          <StatusChip tone={(state?.tone ?? "neutral")} size="sm">
            {state?.word ?? r.state}
          </StatusChip>
        );
      },
    },
    {
      id: "record",
      header: "About",
      cell: (r) => (
        <RecordLinkText recordId={r.record_id}>{r.record_label}</RecordLinkText>
      ),
    },
    {
      id: "raised",
      header: "Raised",
      sortable: true,
      cell: (r) => <Raised at={r.raised_at} />,
    },
    { id: "responses", header: "Responses", numeric: true, sortable: true, cell: (r) => String(r.responses.length) },
  ];

  if (mayRead === false) {
    return (
      <ScreenFrame screen={screen} title="Assurance" crumb="what the assurance provider asked" bodyScrolls>
        <TablePage
          icon={ClipboardText}
          title="Approvals & audit"
          label="Assurance"
          actions={<InfoPopover infoKey="audit.assurance" />}
          state={
            <p className={cls(own.whoCan)} data-testid="assurance-who-can-read">
              <strong>This role does not read the assurance queue.</strong>
            </p>
          }
        />
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame
      screen={screen}
      title="Assurance"
      crumb="what the assurance provider asked, what they were told, and who closed it"
      bodyScrolls
    >
      <TablePage
        icon={ClipboardText}
        title="Approvals & audit"
        label="Assurance"
        actions={
          <>
            {/* The queue as a table - an auditor's working papers (gate 27). The file is exactly
                what the grid shows: same clamp, one row per answer, people as ids. It says in its
                first row that the chain does not prove the wording. Shown only when the queue was
                read, so the link is never offered beside a failed read. */}
            {queue.data && queue.data.length > 0 ? (
              <a
                href={apiUrl("/api/v1/assurance/requests/export")}
                download
                data-testid="assurance-export"
                className={cls(own.exportLink)}
              >
                <DownloadSimple size={14} weight="bold" aria-hidden="true" />
                Export CSV
              </a>
            ) : null}
            {mayRaise ? (
              <ActionButton
                variant="primary"
                data-action="add"
                data-testid="assurance-raise-open"
                onClick={() => {
                  setRaiseOpen(true);
                  setRaiseDismissed(false);
                }}
              >
                Raise a request
              </ActionButton>
            ) : null}
            <InfoPopover infoKey="audit.assurance" />
          </>
        }
        tiles={
          known && known.length > 0 ? (
            <TileStrip label="Where the queue stands">
              <Tile
                value={String(waitingOnCompany)}
                label="Waiting on the company"
                testId="assurance-waiting-company"
              />
              <Tile
                value={String(waitingOnReviewer)}
                label={engaged ? "Waiting on you" : "Waiting on the requester"}
                testId="assurance-waiting-reviewer"
              />
              <Tile
                value={String(resolved + withdrawn)}
                unit={`(${String(resolved)} resolved, ${String(withdrawn)} withdrawn)`}
                label="Closed"
                testId="assurance-closed"
              />
            </TileStrip>
          ) : undefined
        }
        filters={
          queue.error || (queue.data && requests.length === 0) ? undefined : (
            <FilterLine
              filters={[
                {
                  id: "state",
                  label: "State",
                  mode: "single",
                  options: STATES.map((value) => ({
                    value,
                    label: STATE[value]?.word ?? value,
                    count: requests.filter((r) => r.state === value).length,
                  })),
                },
              ]}
              state={line}
              onChange={setLine}
              resultCount={queue.data ? { shown: rows.length, total: requests.length, noun: "requests" } : undefined}
              actions={<InfoPopover infoKey="audit.assurance-people" />}
            />
          )
        }
        table={
          queue.error ? undefined : (
            <>
              {/* An engaged Auditor's list is shorter than the queue, and the screen says so: the
                  server filters rather than refuses (ADR-0029), so a short list would read as a short
                  queue. The rest of the sentence - what is not named, and why - is one control away. */}
              {engaged ? (
                <p className={cls(own.note, own.engaged)} data-testid="assurance-engagement-note">
                  <strong>This is the queue within your engagement, not the whole queue.</strong>{" "}
                  <InfoPopover infoKey="audit.assurance-engagement" />
                </p>
              ) : null}
              {me && !mayRaise ? (
                <p className={cls(own.whoCan)} data-testid="assurance-raise-who-can">
                  <strong>An Auditor or an Admin raises a request.</strong>{" "}
                  <InfoPopover infoKey="audit.assurance-raise" />
                </p>
              ) : null}
              <GridTable
                caption="Assurance requests"
                columns={columns}
                rows={sorted}
                rowKey={(r) => r.id}
                rowAttributes={(r) => ({ "data-request-id": r.id, "data-state": r.state })}
                loading={queue.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={
                  <EmptyState
                    compact
                    testId={requests.length > 0 ? "assurance-filtered-empty" : "assurance-empty"}
                    icon={requests.length > 0 ? undefined : ClipboardText}
                    title={
                      requests.length > 0
                        ? stateFilter
                          ? `No request is ${stateFilter}. ${String(requests.length)} ${requests.length === 1 ? "is" : "are"} not shown by this filter.`
                          : "No request matches the search."
                        : engaged
                          ? "No request inside your engagement yet"
                          : "No requests yet"
                    }
                    action={
                      requests.length > 0 || !me ? undefined : (
                        // The sentence naming whose act it is stands above the table for a role that
                        // may not raise one, so none is repeated here.
                        <EmptyAction
                          label="Raise a request"
                          allowed={mayRaise}
                          onClick={() => {
                            setRaiseOpen(true);
                            setRaiseDismissed(false);
                          }}
                          testId="assurance-empty-raise"
                        />
                      )
                    }
                  >
                    {requests.length > 0 ? null : "An auditor's questions about a record are tracked here."}
                  </EmptyState>
                }
                testId="assurance-queue"
              />
            </>
          )
        }
        state={
          queue.error ? (
            <ReadFailed
              of="The assurance queue"
              error={queue.error}
              onRetry={() => void queue.refetch()}
            />
          ) : undefined
        }
      />

      <Drawer
        open={opened !== undefined}
        onClose={() => setOpenId(null)}
        title="Assurance request"
        width="lg"
        testId="assurance-request-drawer"
      >
        {opened ? (
          <ol className={cls(own.queue)}>
            <RequestCard request={opened} role={role} userId={me?.user_id ?? ""} />
          </ol>
        ) : null}
      </Drawer>

      <Drawer
        open={raiseShown}
        onClose={() => {
          setRaiseOpen(false);
          setRaiseDismissed(true);
        }}
        title="Raise a request"
        width="md"
        testId="assurance-raise-drawer"
      >
        <RaiseForm />
      </Drawer>
    </ScreenFrame>
  );
}

/** Its own component so the reader's preferences are read only when there is a date to place. */
function Raised({ at }: { at: string }) {
  const reader = useReader();
  return <time dateTime={at}>{instantDate(at, reader)}</time>;
}

function RecordLinkText({ recordId, children }: { recordId: string; children: string }) {
  return (
    <Link
      className={cls(shared.fixLink)}
      to="/records/$recordId"
      params={{ recordId }}
      search={keepSearch}
      data-record-id={recordId}
    >
      {children}
    </Link>
  );
}

function RequestCard({
  request,
  role,
  userId,
}: {
  request: AssuranceRequest;
  role: string;
  userId: string;
}) {
  const reader = useReader();
  const state = STATE[request.state] ?? {
    word: request.state,
    whose: "a state this screen does not recognise, so nothing here is a claim about it",
    tone: "neutral" as const,
  };
  const can = standing(role, userId, request);
  const live = isLive(request);
  const answered = request.state === "answered";

  return (
    <li
      className={cls(own.request)}
      data-testid="assurance-request"
      data-request-id={request.id}
      data-state={request.state}
    >
      <div className={cls(own.requestHead)}>
        <span className={cls(own.kind)}>{KIND_LABEL[request.kind] ?? request.kind}</span>
        {request.reference ? <span className={cls(own.reference)}>{request.reference}</span> : null}
        <span className={cls(own.stateTag)} data-testid="assurance-state">
          <StatusPill tone={state.tone}>{state.word}</StatusPill>
          <span className={cls(own.whose)}> — {state.whose}</span>
        </span>
      </div>

      <p className={cls(own.question)} data-testid="assurance-question">
        {request.question}
      </p>

      <p className={cls(own.facts)}>
        About{" "}
        <RecordLinkText recordId={request.record_id}>{request.record_label}</RecordLinkText>
        {" · "}raised{" "}
        <time dateTime={request.raised_at}>{instantDate(request.raised_at, reader)}</time> by{" "}
        <span className={cls(own.id)} data-testid="assurance-raised-by">
          {request.raised_by}
        </span>
        {request.raised_by === userId ? " (you)" : ""}
      </p>

      <Thread responses={request.responses} />

      {request.resolution ? (
        <p className={cls(own.closure)} data-testid="assurance-closure">
          <strong>{state.word}</strong>
          {request.closed_at ? (
            <>
              {" "}
              <time dateTime={request.closed_at}>{instantDate(request.closed_at, reader)}</time>
            </>
          ) : null}
          {request.closed_by ? (
            <>
              {" "}
              by <span className={cls(own.id)}>{request.closed_by}</span>
            </>
          ) : null}
          : {request.resolution}
        </p>
      ) : null}

      {live ? (
        <div className={cls(own.actions)}>
          {can.answer === null ? (
            <RespondForm request={request} />
          ) : (
            <p className={cls(own.whoCan)} data-testid="assurance-who-can-answer">
              <strong>You do not answer this.</strong> {can.answer}
            </p>
          )}
          {can.close === null ? (
            <CloseForm request={request} canResolve={answered} />
          ) : (
            <p className={cls(own.whoCan)} data-testid="assurance-who-can-close">
              <strong>You do not close this.</strong> {can.close}
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

function Thread({ responses }: { responses: readonly AssuranceResponse[] }) {
  const reader = useReader();
  if (responses.length === 0) {
    return (
      <p className={cls(own.thread, own.quietLine)} data-testid="assurance-no-responses">
        Nothing has been said in reply.
      </p>
    );
  }
  return (
    <div className={cls(own.thread)} data-testid="assurance-thread">
      <p className={cls(own.threadHead)}>
        {responses.length === 1
          ? "One response"
          : `${responses.length} responses, oldest first — an earlier answer that was later amended stays here`}
      </p>
      <ol className={cls(own.responses)}>
        {responses.map((response, index) => (
          <li key={response.id} className={cls(own.response)} data-testid="assurance-response">
            <p className={cls(own.responseMeta)}>
              Response {index + 1} ·{" "}
              <time dateTime={response.responded_at}>{instantDate(response.responded_at, reader)}</time> by{" "}
              <span className={cls(own.id)}>{response.responded_by}</span>
            </p>
            <p className={cls(own.responseBody)}>{response.body}</p>
            <Citation response={response} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function Citation({ response }: { response: AssuranceResponse }) {
  if (citationWithheld(response)) {
    // **Printed, not blank.** A null here would read as *no evidence was cited*, which is false;
    // the server withheld the id because the record is outside this reader's engagement.
    return (
      <p className={cls(own.citation, own.withheld)} data-testid="assurance-citation-withheld">
        Cites a record outside your engagement. It is not named here, and it is not something you can
        open.
      </p>
    );
  }
  if (!response.cites_record_id) return null;
  return (
    <p className={cls(own.citation)} data-testid="assurance-citation">
      Cites{" "}
      <RecordLinkText recordId={response.cites_record_id}>
        {response.cites_record_label ?? "this record"}
      </RecordLinkText>
    </p>
  );
}

function RaiseForm() {
  const raise = useRaiseRequest();
  // **Arrived at from a record** (gate 27): "Raise a question about this record" on a receipt or a
  // record page carries the record's id here, so an auditor does not have to copy an id out of an
  // address bar. Read from the location rather than the validator's output (`useSearch` with
  // `strict: false`), and used only if it is an id - a hand-edited address is not trusted to be one.
  // The picker shows it by name; the server resolves and clamps the id exactly as it does a chosen
  // one, so this is a convenience and not a second way in.
  const arrived: unknown = useSearch({ strict: false }).record;
  const arrivedId = typeof arrived === "string" && UUID_PATTERN.test(arrived.trim()) ? arrived.trim() : null;
  // `undefined` is "not touched": the record the form arrived with, once the server has named it,
  // stands until the person chooses another (`null`) or picks one. Derived, not copied by an effect.
  const arrivedOption = useRecordOption(arrivedId);
  const [picked, setPicked] = useState<RecordOption | null | undefined>(undefined);
  // A failed read of the arrival is not "the server named it": the person chooses, and the list
  // below says if it cannot be read either.
  const arrivedRecord = arrivedOption.isSuccess ? arrivedOption.data : null;
  const record = picked === undefined ? arrivedRecord : picked;
  const waitingOnArrival = arrivedId !== null && picked === undefined && arrivedOption.isPending;
  // A link that says "raise a question" opens as a question; a blank form keeps the first kind.
  const [kind, setKind] = useState<RequestKindName>(arrivedId ? "question" : "sample");
  const [reference, setReference] = useState("");
  const [question, setQuestion] = useState("");

  const ready = record !== null && question.trim().length > 0 && !raise.isPending;
  const chosen = KINDS.find((k) => k.kind === kind);

  return (
    <form
      className={cls(own.form)}
      data-testid="assurance-raise-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        raise.mutate(
          { recordId: record.id, kind, reference, question },
          {
            onSuccess: () => {
              setPicked(null);
              setReference("");
              setQuestion("");
            },
          },
        );
      }}
    >
      <RecordPicker chosen={record} onChoose={setPicked} searchEnabled={!waitingOnArrival} />

      <label className={cls(shared.field)}>
        What kind of request
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as RequestKindName)}
          data-testid="assurance-raise-kind"
        >
          {KINDS.map((k) => (
            <option key={k.kind} value={k.kind}>
              {k.label}
            </option>
          ))}
        </select>
      </label>
      <p className={cls(own.help)} data-testid="assurance-raise-kind-help">
        {chosen?.answeredWith}
      </p>

      <label className={cls(shared.field)}>
        Your reference (optional)
        <input
          value={reference}
          maxLength={200}
          onChange={(event) => setReference(event.target.value)}
          data-testid="assurance-raise-reference"
        />
      </label>

      <label className={cls(shared.field)}>
        The question
        <textarea
          className={cls(own.textarea)}
          value={question}
          maxLength={4000}
          rows={4}
          onChange={(event) => setQuestion(event.target.value)}
          data-testid="assurance-raise-question"
        />
      </label>

      <button type="submit" className={cls(shared.primary)} disabled={!ready}>
        {raise.isPending ? "Raising…" : "Raise request"}
      </button>
      {ready || raise.isPending ? null : (
        <p className={cls(own.help)} data-testid="assurance-raise-missing">
          {record === null ? "Choose a record" : "Write the question"} to raise it.
        </p>
      )}
      <WriteFailed error={raise.error} of="that request" />
      {raise.isSuccess ? (
        <p role="status" className={cls(own.help)} data-testid="assurance-raised">
          Raised. It is open until the organisation answers.
        </p>
      ) : null}
    </form>
  );
}

function RespondForm({ request }: { request: AssuranceRequest }) {
  const respond = useRespond();
  const [body, setBody] = useState("");
  const [cites, setCites] = useState("");

  const sample = request.kind === "sample";
  const citeTrim = cites.trim();
  const citeOk = citeTrim === "" ? !sample : UUID_PATTERN.test(citeTrim);
  const loop = citeTrim !== "" && citeTrim.toLowerCase() === request.record_id.toLowerCase();
  const ready = body.trim().length > 0 && citeOk && !loop && !respond.isPending;

  return (
    <form
      className={cls(own.form)}
      data-testid="assurance-respond-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        respond.mutate(
          { requestId: request.id, body, citesRecordId: cites },
          {
            onSuccess: () => {
              setBody("");
              setCites("");
            },
          },
        );
      }}
    >
      <label className={cls(shared.field)}>
        {request.state === "answered" ? "Amend or add to the answer" : "Answer for the organisation"}
        <textarea
          className={cls(own.textarea)}
          value={body}
          maxLength={8000}
          rows={3}
          onChange={(event) => setBody(event.target.value)}
          data-testid="assurance-respond-body"
        />
      </label>
      <label className={cls(shared.field)}>
        {sample ? "Record that is the evidence (required)" : "Record that is the evidence (optional)"}
        <input
          value={cites}
          onChange={(event) => setCites(event.target.value)}
          spellCheck={false}
          autoComplete="off"
          data-testid="assurance-respond-cites"
        />
      </label>
      <p className={cls(own.help)} data-testid="assurance-respond-help">
        {sample
          ? "A sample is answered with the evidence itself — a description is not something a provider can test. Cite the document, activity record or filing underneath the one asked about."
          : "A question or an assertion may be answered in words alone."}
        {loop ? " That is the record the question is about, which answers nothing." : ""}
        {citeTrim !== "" && !UUID_PATTERN.test(citeTrim) ? " That is not an id." : ""}
      </p>
      <p className={cls(own.help)}>
        A correction is a further response, not an edit: the first answer stays in the thread.
      </p>
      <button type="submit" className={cls(shared.secondary)} disabled={!ready}>
        {respond.isPending ? "Sending…" : "Send response"}
      </button>
      <WriteFailed error={respond.error} of="that response" />
    </form>
  );
}

function CloseForm({ request, canResolve }: { request: AssuranceRequest; canResolve: boolean }) {
  const resolve = useResolve();
  const withdraw = useWithdraw();
  const [text, setText] = useState("");
  const pending = resolve.isPending || withdraw.isPending;
  const ready = text.trim().length > 0 && !pending;

  return (
    <div className={cls(own.form)} data-testid="assurance-close-form">
      <label className={cls(shared.field)}>
        Why you are closing it
        <textarea
          className={cls(own.textarea)}
          value={text}
          maxLength={4000}
          rows={2}
          onChange={(event) => setText(event.target.value)}
          data-testid="assurance-close-text"
        />
      </label>
      <p className={cls(own.help)}>
        Closing needs a reason, and it is recorded with who closed it and when. A closed request is
        not reopened; a reopened question is a new request.
      </p>
      <div className={cls(own.buttons)}>
        {canResolve ? (
          <button
            type="button"
            className={cls(shared.primary)}
            disabled={!ready}
            data-testid="assurance-resolve"
            onClick={() => resolve.mutate({ requestId: request.id, resolution: text })}
          >
            {resolve.isPending ? "Resolving…" : "Resolve — the answer was enough"}
          </button>
        ) : (
          <p className={cls(own.help)} data-testid="assurance-cannot-resolve-yet">
            Nothing has been answered yet, so there is nothing to resolve. Resolving an unanswered
            question would record a satisfaction that did not happen.
          </p>
        )}
        <button
          type="button"
          className={cls(shared.secondary)}
          disabled={!ready}
          data-testid="assurance-withdraw"
          onClick={() => withdraw.mutate({ requestId: request.id, reason: text })}
        >
          {withdraw.isPending ? "Withdrawing…" : "Withdraw — no longer needed"}
        </button>
      </div>
      <WriteFailed error={resolve.error} of="that resolution" />
      <WriteFailed error={withdraw.error} of="that withdrawal" />
    </div>
  );
}
